import React, { useState } from "react";
import { Star, CheckCircle, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

const EmpFeedbackForm = () => {
  const [ratings, setRatings] = useState({
    quality: 0,
    relevance: 0,
    facilitator: 0,
    application: 0,
    mood: 0,
  });

  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const questions = [
    {
      id: "quality",
      text: "How would you rate the overall quality of the well-being session?",
      description: "1 = Poor, 5 = Excellent",
    },
    {
      id: "relevance",
      text: "How relevant was the content to your personal well-being or work-life balance?",
      description: "1 = Not Relevant, 5 = Highly Relevant",
    },
    {
      id: "facilitator",
      text: "How effective was the facilitator (Srilatha Rakesh) in delivering the session?",
      description: "1 = Not Effective, 5 = Very Effective",
    },
    {
      id: "application",
      text: "How likely are you to apply what you learned in your daily life?",
      description: "1 = Not Likely, 5 = Very Likely",
    },
    {
      id: "mood",
      text: "How much did the session help improve your current mood or mindset?",
      description: "1 = Not at All, 5 = Significantly",
    },
  ];

  const handleStarClick = (questionId, rating) => {
    setRatings((prev) => ({
      ...prev,
      [questionId]: rating,
    }));

    // Clear error for this question when user provides a rating
    if (errors[questionId]) {
      setErrors((prev) => ({
        ...prev,
        [questionId]: false,
      }));
    }
  };

  const handleSubmit = () => {
    // Reset previous errors
    setErrors({});
    setShowErrors(false);

    // Validate all questions
    const newErrors = {};
    let hasErrors = false;

    questions.forEach((question) => {
      if (!ratings[question.id] || ratings[question.id] === 0) {
        newErrors[question.id] = true;
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setErrors(newErrors);
      setShowErrors(true);

      // Scroll to first error
      const firstErrorElement = document.querySelector(".error-border");
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }

      return;
    }

    // All validation passed
    console.log("Feedback submitted:", { ratings, comment });

    setIsLoading(true);
    setTimeout(() => {
      const rootElement = document.querySelector(".justify-center");
      rootElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 100);

    const formData1 = new URLSearchParams();
    formData1.append("type", "Well being form");
    formData1.append("quality", ratings.quality);
    formData1.append("relevance", ratings.relevance);
    formData1.append("facilitator", ratings.facilitator);
    formData1.append("application", ratings.application);
    formData1.append("mood", ratings.mood);
    formData1.append("comment", comment);

    fetch("api", {
      method: "post",
      body: "",
    })
      .then((res) => res.json)
      .then((data) => {})
      .catch((err) => {});

    fetch(
      "https://script.google.com/macros/s/AKfycbyrXpd_fNWCWwK4Z8VC4n-L7izyXFHjwL_nbjrMDkfVVL6vuxcWXb2rcs95CPEXS-3tqw/exec",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formData1,
      }
    )
      .then((response) => response.json())
      .then((data) => {
        setIsLoading(false);
        setSubmitted(true);
        console.log("Success:", data);
      })
      .catch((error) => {
        setIsLoading(false);
        setSubmitted(true);
        console.error("Fetch error:", error);
      });
  };
  const handleClickHome = () => {
    navigate("/");
  };

  const StarRating = ({ questionId, currentRating }) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => handleStarClick(questionId, star)}
            className="transition-colors duration-200 hover:scale-110 transform"
          >
            <Star
              size={28}
              className={`${
                star <= currentRating
                  ? "fill-yellow-400 text-yellow-400"
                  : "text-gray-300 hover:text-yellow-300"
              }`}
            />
          </button>
        ))}
        <span className="ml-3 text-sm text-gray-600 font-medium">
          {currentRating > 0 ? `${currentRating}/5` : "Not rated"}
        </span>
      </div>
    );
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="mb-6">
            <CheckCircle className="mx-auto text-green-500 mb-4" size={64} />
            <h2 className="text-3xl font-bold text-gray-800 mb-2">
              Thank You!
            </h2>
            <p className="text-gray-600 text-lg">
              Your feedback has been submitted successfully. We appreciate your
              time and insights!
            </p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-800 font-medium">
              Your responses will help us improve future well-being sessions.
            </p>
            {comment && (
              <p className="text-green-700 text-sm mt-2">
                Thank you for your additional comments and suggestions!
              </p>
            )}
          </div>
          <button
            onClick={handleClickHome}
            className="w-full mt-[10px] bg-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors flex items-center justify-center"
          >
            Home
          </button>
        </div>
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-30 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">
            Submitting your feedback...
          </h2>
          <p className="text-gray-600">
            Please wait while we process your responses
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Well-being Session Feedback
            </h1>
            <p className="text-gray-600 text-lg">
              Please rate your experience with today's session
            </p>
          </div>

          <div className="space-y-8">
            {questions.map((question, index) => (
              <div
                key={question.id}
                className={`rounded-xl p-6 transition-all duration-300 ${
                  errors[question.id]
                    ? "bg-red-50 border-2 border-red-200 error-border"
                    : "bg-gray-50"
                }`}
              >
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    {index + 1}. {question.text}
                  </h3>
                  <p className="text-sm text-gray-500 italic">
                    {question.description}
                  </p>
                  {errors[question.id] && showErrors && (
                    <div className="mt-2 flex items-center text-red-600 text-sm">
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Please provide a rating for this question
                    </div>
                  )}
                </div>

                <StarRating
                  questionId={question.id}
                  currentRating={ratings[question.id]}
                />
              </div>
            ))}

            {/* Optional Comments Section */}
            <div className=" rounded-xl p-6  bg-gray-50">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  6. Additional Comments or Suggestions (Optional)
                </h3>
                <p className="text-sm text-gray-500 italic">
                  Please share any additional feedback, suggestions, or comments
                  about the session
                </p>
              </div>

              <div className="relative">
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Share your thoughts, suggestions, or any additional feedback..."
                  className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none transition-all duration-200"
                  rows="4"
                  maxLength="500"
                />
                <div className="absolute bottom-2 right-2 text-xs text-gray-400">
                  {comment.length}/500
                </div>
              </div>
            </div>

            <div className="text-center pt-6">
              {showErrors && Object.keys(errors).length > 0 && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center justify-center text-red-800">
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="font-medium">
                      Please answer all questions before submitting (
                      {Object.keys(errors).length} question
                      {Object.keys(errors).length > 1 ? "s" : ""} missing)
                    </span>
                  </div>
                </div>
              )}

              <button
                onClick={handleSubmit}
                className="w-full bg-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors flex items-center justify-center"
              >
                Submit Feedback
              </button>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              <span className="text-red-500">*</span> Rating questions are
              required. Comments are optional but appreciated.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmpFeedbackForm;
